import { PRIVATE_PATH_JS } from './private-paths'
import { GA_SUPPRESS_JS } from './ga-suppression'
import { consentModeDefaultJs, googleTagBlockedJs } from './consent-defaults'
import { CONSENT_REGION_COOKIE, CONSENT_REGION_UNRESTRICTED_VALUE } from './consent-regions'
import { MARKET_SLUG_MAX, VISITOR_INTENTS, VISITOR_INTENT_STORAGE_KEY } from './visitor-intent'
import { CONSENT_COOKIE } from '@/lib/identity/consent'
import { ga4SafeUrlFnJs } from '@/app/api/visitors/track/strip-identity'

/**
 * The page address GA4 may see, as one JS function: stripGa4UrlParams
 * (strip-identity.ts), the same rule the server mirror and every Measurement
 * Protocol event use. Identity params, `rr_doc` and a person's typed text
 * (`/sell?address=`, `/contact?inquiry=`) removed, `/cma/<slug>` made
 * `/cma/[document]`. Left untouched when there is nothing to remove, so the
 * URL's own encoding survives. Defined once in the bootstrap and called for
 * location.href and document.referrer.
 */
const GA4_SAFE_URL_FN_JS = ga4SafeUrlFnJs()

/**
 * Inside the GTM loader, before gtm.js: `pl` and `pr` are the safe location and
 * referrer, and `gtag('set', { page_location, page_referrer })` makes EVERY hit
 * on this page carry them, not just the page_view: the "GA4 - dataLayer events"
 * tag and the Google tag's own events (form_start, scroll, user_engagement)
 * otherwise send the browser's raw location and referrer. Verified on
 * production 2026-10-14 with every Google request blocked: a dataLayer event,
 * scroll_depth and enhanced measurement's form_start all took dl= and dr= from
 * this command. No GTM change is needed. PageViewTracker repeats it on every
 * client-side navigation. An empty referrer stays absent.
 */
const GA4_SET_PAGE_JS = `var ga=${GA4_SAFE_URL_FN_JS},pl=ga(location.href),pr=ga(d.referrer);if(pl){var o={page_location:pl};if(pr)o.page_referrer=pr;gtag('set',o)}`

/**
 * The stored visitor intent and market (lib/analytics/visitor-intent.ts), as one
 * JS expression that evaluates to `{ visitor_intent?, primary_market? }`.
 *
 * The first-paint page_view is pushed here, before React hydrates, so it can
 * only carry what the browser stored on an earlier page; PageViewTracker records
 * this page on hydration and pushes the updated values for every later hit.
 * Read only when analytics consent holds, the same test as hasAnalyticsConsent:
 * GPC off, and a stored banner answer with analytics on, or no answer (no cookie,
 * or an empty one, as parseConsentCookie reads it) outside a restricted region
 * (`rr_cr=0`). Values are re-checked against the label list and
 * the slug shape. Any failure (blocked storage, bad JSON) yields `{}`.
 */
const VISITOR_INTENT_JS = `(function(){try{if(navigator.globalPrivacyControl===true)return{};var k=document.cookie||'',c=k.match(/(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)/),r=c?c[1].trim():'';if(r){var a;try{var p=JSON.parse(decodeURIComponent(r));a=!!(p&&p.analytics)}catch(e){a=r==='all'}if(!a)return{}}else if(!/(?:^|;\\s*)${CONSENT_REGION_COOKIE}=${CONSENT_REGION_UNRESTRICTED_VALUE}(?:;|$)/.test(k))return{};var m=JSON.parse(window.localStorage.getItem('${VISITOR_INTENT_STORAGE_KEY}')||'null'),o={};if(m&&typeof m.intent==='string'&&/^(${VISITOR_INTENTS.join('|')})$/.test(m.intent))o.visitor_intent=m.intent;if(m&&typeof m.primaryMarket==='string'&&/^[a-z0-9-]{1,${MARKET_SLUG_MAX}}$/.test(m.primaryMarket))o.primary_market=m.primaryMarket;return o}catch(e){return{}}})()`

/**
 * The inline Google Tag Manager bootstrap, as one string.
 *
 * WHY a module of its own: on 2026-09-17 (f1e2a90f9) an edit left
 * `push({'gtm.start':}}` in the template inside components/GTMHead.tsx. A script
 * with a parse error runs nothing, so gtm.js never loaded and the browser GA4
 * stream (session_start, first_visit, engagement, every client event) read
 * zero from 2026-09-18 until the 2026-09-22 visibility audit found it
 * (TRACK-2). The gates only regex the file for strings, so they passed.
 * Building the script here lets gtm-bootstrap.test.ts parse the exact string
 * the page ships, which turns that class of break into a failed test.
 */
export function gtmBootstrapScript(pageType: string, gtmId: string): string {
  // gtm.js never loads on a page whose address carries a secret (private-paths.ts);
  // GTMHead already renders nothing there, and this holds even if it did.
  // Nor on a page GA4 must not count (ga-suppression.ts, Matt 2026-10-05): /admin,
  // a host that is not ryan-realty.com, automation, or a signed-in broker's
  // browser. Decided in the browser before gtm.js is requested, so a suppressed
  // page loads no Google tag at all.
  // Nor after a stored decline or under GPC (G3, googleTagBlockedJs): an opt-out
  // stops GA4 entirely, with no cookieless Consent Mode pings. Checked first
  // inside the loader, so the private-path guard keeps the shape
  // check-tracking-policy.mjs requires.
  //
  // The first-paint page_view is pushed here, right after gtm.js (GTM version 9,
  // 2026-10-14): GTM's "GA4 - page_view" tag sends it with page_type, broker_slug
  // and a GA4-safe page_location (no person data, Matt 2026-10-14), and the
  // Google tag no longer sends its own (send_page_view false). Just before
  // gtm.js, `gtag('set')` gives every other hit the same safe page_location and
  // page_referrer (GA4_SET_PAGE_JS). Pushing it inline, not from a React effect,
  // keeps a visitor who leaves before hydration counted, as the Google tag did.
  // PageViewTracker sends the later client-side navigations. The page_view also
  // carries the stored visitor_intent / primary_market (VISITOR_INTENT_JS), which
  // GTM version 10 sends as GA4 user properties.
  const safePageType = pageType.replace(/\\/g, '\\\\').replace(/'/g, "\\'")
  return `window.dataLayer=window.dataLayer||[];
function gtag(){dataLayer.push(arguments);}
${consentModeDefaultJs()}
window.dataLayer.push({page_type:'${safePageType}'});
(function(){try{var a=new URLSearchParams(location.search||'').get('agent');if(!a){var m=document.cookie.match(/(?:^|; )rr_agent_attribution=([^;]*)/);if(m){try{var j=JSON.parse(decodeURIComponent(m[1]));a=j&&j.slug}catch(e){a=decodeURIComponent(m[1])}}}if(!a)return;a=String(a).trim().toLowerCase();var map={matt:'matt','matt-ryan':'matt',rebecca:'rebecca','rebecca-peterson':'rebecca',paul:'paul','paul-stevenson':'paul'};var s=map[a];if(!s)return;gtag('set','user_properties',{assigned_broker:s});window.dataLayer.push({broker_slug:s,assigned_broker:s})}catch(e){}})();
if(!${PRIVATE_PATH_JS}&&!${GA_SUPPRESS_JS})(function(w,d,s,l,i){if(${googleTagBlockedJs()})return;w[l]=w[l]||[];${GA4_SET_PAGE_JS}w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});w[l].push(Object.assign({event:'page_view',page_type:'${safePageType}',page_location:pl},${VISITOR_INTENT_JS}));var f=d.getElementsByTagName(s)[0], // hydration-safe — GTM bootstrap stamp
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;if(f&&f.parentNode)f.parentNode.insertBefore(j,f);else d.head.appendChild(j);
})(window,document,'script','dataLayer','${gtmId}');`
}
